import React, { useCallback, useEffect, useRef } from 'react';
import { Button, Modal } from 'antd';

import {
  MARKING_PORTAL_PIN_EXPIRATION_MINUTES,
  MARKING_PORTAL_PIN_LENGTH,
} from '@src/models/interfaces/markingPortal.interface';
import IntlMessages from '@src/util/IntlMessages';

import styles from '../styles.module.less';

const PIN_CHAR_PATTERN = /^[A-Z0-9]$/;

interface MarkingPortalPinModalProps {
  open: boolean;
  email: string;
  pin: string;
  isSubmitting: boolean;
  isResending: boolean;
  errorMessageId?: string;
  onPinChange: (pin: string) => void;
  onSubmit: () => void;
  onResend: () => void;
  onClose: () => void;
}

const buildPinDigits = (pin: string): string[] => {
  const digits = pin.toUpperCase().split('').slice(0, MARKING_PORTAL_PIN_LENGTH);

  while (digits.length < MARKING_PORTAL_PIN_LENGTH) {
    digits.push('');
  }

  return digits;
};

export const MarkingPortalPinModal: React.FC<MarkingPortalPinModalProps> = ({
  open,
  email,
  pin,
  isSubmitting,
  isResending,
  errorMessageId,
  onPinChange,
  onSubmit,
  onResend,
  onClose,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const pinDigits = buildPinDigits(pin);
  const isPinComplete = pin.length === MARKING_PORTAL_PIN_LENGTH;

  const updatePinAtIndex = useCallback(
    (index: number, value: string) => {
      const nextDigits = [...pinDigits];
      nextDigits[index] = value;
      onPinChange(nextDigits.join('').slice(0, MARKING_PORTAL_PIN_LENGTH));
    },
    [onPinChange, pinDigits],
  );

  const focusInput = useCallback((index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  }, []);

  const handleDigitChange = (index: number, rawValue: string) => {
    const value = rawValue.toUpperCase();

    if (!value) {
      updatePinAtIndex(index, '');
      return;
    }

    const characters = value.split('').filter(char => PIN_CHAR_PATTERN.test(char));

    if (characters.length === 0) {
      return;
    }

    if (characters.length === 1) {
      updatePinAtIndex(index, characters[0]);
      if (index < MARKING_PORTAL_PIN_LENGTH - 1) {
        focusInput(index + 1);
      }
      return;
    }

    const nextDigits = [...pinDigits];
    let nextIndex = index;

    characters.forEach(character => {
      if (nextIndex >= MARKING_PORTAL_PIN_LENGTH) {
        return;
      }
      nextDigits[nextIndex] = character;
      nextIndex += 1;
    });

    onPinChange(nextDigits.join('').slice(0, MARKING_PORTAL_PIN_LENGTH));

    if (nextIndex < MARKING_PORTAL_PIN_LENGTH) {
      focusInput(nextIndex);
    } else {
      focusInput(MARKING_PORTAL_PIN_LENGTH - 1);
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !pinDigits[index] && index > 0) {
      updatePinAtIndex(index - 1, '');
      focusInput(index - 1);
      event.preventDefault();
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
      event.preventDefault();
    }

    if (event.key === 'ArrowRight' && index < MARKING_PORTAL_PIN_LENGTH - 1) {
      focusInput(index + 1);
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const timeoutId = window.setTimeout(() => focusInput(0), 0);
    return () => window.clearTimeout(timeoutId);
  }, [focusInput, open]);

  return (
    <Modal
      visible={open}
      footer={null}
      closable
      maskClosable
      centered
      width={480}
      className={styles.pinModal}
      onCancel={onClose}
    >
      <div className={styles.pinModalContent}>
        <h2 className={styles.pinModalTitle}>
          <IntlMessages id="markingPortal.pin.modal.title" />
        </h2>
        <p className={styles.pinModalSubtitle}>
          <IntlMessages id="markingPortal.pin.modal.subtitle" />
        </p>
        <p className={styles.pinModalEmail}>{email}</p>
        <p className={styles.pinModalExpiresIn}>
          <IntlMessages
            id="markingPortal.pin.modal.expiresIn"
            values={{ minutes: MARKING_PORTAL_PIN_EXPIRATION_MINUTES }}
          />
        </p>

        <div className={styles.pinInputRow} role="group" aria-label={email}>
          {pinDigits.map((digit, index) => (
            <input
              key={index}
              ref={element => {
                inputRefs.current[index] = element;
              }}
              className={styles.pinInputCell}
              value={digit}
              maxLength={MARKING_PORTAL_PIN_LENGTH}
              autoComplete="one-time-code"
              inputMode="text"
              disabled={isSubmitting || isResending}
              aria-label={`${index + 1}`}
              onChange={event => handleDigitChange(index, event.target.value)}
              onKeyDown={event => handleKeyDown(index, event)}
            />
          ))}
        </div>

        {errorMessageId && (
          <p className={styles.pinModalError}>
            <IntlMessages id={errorMessageId} />
          </p>
        )}

        <Button
          type="primary"
          block
          size="large"
          loading={isSubmitting}
          disabled={!isPinComplete || isResending}
          className={styles.pinModalPrimaryButton}
          onClick={onSubmit}
        >
          <IntlMessages id="markingPortal.pin.modal.submit" />
        </Button>

        <div className={styles.pinModalResendRow}>
          <Button
            type="link"
            className={styles.linkButton}
            loading={isResending}
            disabled={isSubmitting}
            onClick={onResend}
          >
            <IntlMessages id="markingPortal.pin.modal.resend" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};
